// The Customer Overview tab's account-footprint sections (Phase 2, C-1/C-2/C-3/C-17): Sites and
// lifts, Open work, Open defects, Contracts, Open quotes and Invoices. Everything here is fed by
// the ONE new read, GET /api/customers/:id/overview, so the six sections load and fail together --
// the Contacts card and Risk flags above them stay on the customer's own already-working state in
// CustomerDetailPage, unaffected by anything in this file.
//
// LINK DESTINATIONS, decided deliberately rather than guessed:
//   - A lift always links to /office/lifts/:id, a site to /office/sites/:id -- both existing,
//     working pages, matching the C-3 instruction not to wire a second Lifts-register filter.
//   - A contract's renewal date links to /office/contracts/:id?tab=renewal (a parallel session this
//     phase is adding a Renewal tab at that exact query param); the rest of the row links to the
//     contract's own page. Two separate links, never one nested inside the other.
//   - A quote links to /office/quotes/:id (the Quote Hub, built this phase) and an invoice to
//     /office/invoices/:id -- both already-working record pages.
//   - An "Open work" row (a scheduled/in-progress job or an open call-out) has no record page of
//     its own yet (the office audit records this as still-to-build), so it links to its LIFT when
//     it has one (useful context, and a page that already exists) and renders as plain text,
//     unlinked, when it does not (a lift-less planned repair or emergency call-out) -- disclosed as
//     a deliberate scope decision in the session report rather than guessed at a URL that may not
//     resolve.
//   - "View all" links reuse the SAME ?customerId= forward-filter every other section on this page
//     already uses (Sites/Quotes/Contracts/Invoices); Open defects has no such filter on
//     /office/defects today (mirroring why C-3 did not wire one onto the Lifts register either), so
//     its "View all" opens the plain unfiltered list.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type {
  CustomerOverview,
  CustomerOverviewContract,
  CustomerOverviewDefect,
  CustomerOverviewWorkItem,
} from "../../lib/api";
import JobTypeBadge from "../../components/JobTypeBadge";
import { RecordLink, RecordLinkGroup } from "../../components/RecordLink";
import { StatusPill } from "../../components/StatusPill";
import { Loader } from "../../components/Loader";
import { formatDate } from "../../lib/format";

function gbp(n: number | null | undefined): string {
  if (n == null) return "-";
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}
const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };
const CONTRACT_STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  pending_signature: "Pending signature",
  active: "Active",
  expired: "Expired",
  cancelled: "Cancelled",
};
const QUOTE_STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  pending: "Awaiting customer",
  sent: "Sent",
  opened: "Opened",
  accepted: "Accepted",
};

export function CustomerOverviewTab({ customerId }: { customerId: string }) {
  const [overview, setOverview] = useState<CustomerOverview | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await api.customers.overview(customerId);
      setOverview(res);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") {
    return <Loader shape="panel" label="Loading the account overview..." />;
  }
  if (state === "error" || !overview) {
    return (
      <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
        Could not load the account overview.{" "}
        <button type="button" onClick={() => void load()} className="text-ele-green underline">
          Retry
        </button>
      </p>
    );
  }

  const liftCount = overview.sites.reduce((n, s) => n + s.lifts.length, 0);

  return (
    <div className="space-y-5">
      {/* Sites and lifts */}
      <RecordLinkGroup
        title="Sites and lifts"
        count={overview.sites.length}
        empty="No sites on this customer's account yet."
        action={
          <Link to={`/office/sites?customerId=${encodeURIComponent(customerId)}`} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
            View all
          </Link>
        }
      >
        {overview.sites.map((site) => (
          <div key={site.id}>
            <RecordLink to={`/office/sites/${encodeURIComponent(site.id)}`} label={site.name} meta={site.address ?? undefined} />
            {site.lifts.length > 0 && (
              <div className="ml-4 mt-1.5 space-y-1.5 border-l border-ele-hairline pl-4 sm:ml-6">
                {site.lifts.map((lift) => (
                  <RecordLink
                    key={lift.id}
                    to={`/office/lifts/${encodeURIComponent(lift.id)}`}
                    label={lift.reference}
                    meta={[
                      lift.liftType,
                      lift.nextServiceDue ? `Next service ${formatDate(lift.nextServiceDue)}` : "No service scheduled",
                      lift.lolerDue ? `LOLER due ${formatDate(lift.lolerDue)}` : "No LOLER date on file",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    trailing={<StatusPill domain="liftCompliance" status={lift.complianceStatus} />}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </RecordLinkGroup>
      {overview.sites.length > 0 && liftCount === 0 && (
        <p className="-mt-3 text-xs text-ele-body opacity-70">No lifts on the register at any of these sites yet.</p>
      )}

      {/* Open work: scheduled/in-progress jobs plus open call-outs, merged */}
      <RecordLinkGroup title="Open work" count={overview.openWork.length} empty="No scheduled work or open call-outs.">
        {overview.openWork.map((item) => (
          <OpenWorkRow key={`${item.kind}-${item.id}`} item={item} />
        ))}
      </RecordLinkGroup>

      {/* Open defects */}
      <RecordLinkGroup
        title="Open defects"
        count={overview.openDefects.length}
        empty="No open defects."
        action={
          <Link to="/office/defects" className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
            View all
          </Link>
        }
      >
        {overview.openDefects.map((d) => (
          <OpenDefectRow key={d.id} defect={d} />
        ))}
      </RecordLinkGroup>

      {/* Contracts */}
      <RecordLinkGroup
        title="Contracts"
        count={overview.contracts.length}
        empty="No contracts on this account."
        action={
          <Link to={`/office/contracts?customerId=${encodeURIComponent(customerId)}`} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
            View all
          </Link>
        }
      >
        {overview.contracts.map((c) => (
          <ContractRow key={c.id} contract={c} />
        ))}
      </RecordLinkGroup>

      {/* Open quotes */}
      <RecordLinkGroup
        title="Open quotes"
        count={overview.quotes.length}
        empty="No open quotes."
        action={
          <Link to={`/office/quotes?customerId=${encodeURIComponent(customerId)}`} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
            View all
          </Link>
        }
      >
        {overview.quotes.map((q) => (
          <RecordLink
            key={q.id}
            to={`/office/quotes/${encodeURIComponent(q.id)}`}
            label={q.quoteRef}
            meta={[q.siteName, q.value != null ? `${gbp(q.value)}/yr` : null, QUOTE_STATUS_LABEL[q.status] ?? q.status, q.sentAt ? `Sent ${formatDate(q.sentAt)}` : "Not sent"]
              .filter(Boolean)
              .join(" · ")}
          />
        ))}
      </RecordLinkGroup>

      {/* Invoices */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg tracking-wide text-ele-strong">Invoices</h2>
          <Link to={`/office/invoices?customerId=${encodeURIComponent(customerId)}`} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
            View all
          </Link>
        </div>
        <div className="mb-2 flex flex-wrap gap-4 text-sm">
          <p>
            <span className="text-ele-body opacity-70">Balance owed </span>
            <span className="font-heading text-ele-strong">{gbp(overview.invoices.balanceOwed)}</span>
          </p>
          <p>
            <span className="text-ele-body opacity-70">Overdue </span>
            <span className={`font-heading ${overview.invoices.overdueAmount > 0 ? "text-ele-danger" : "text-ele-strong"}`}>
              {gbp(overview.invoices.overdueAmount)}
            </span>
          </p>
        </div>
        {overview.invoices.items.length === 0 ? (
          <p className="text-sm text-ele-body opacity-70">No outstanding invoices.</p>
        ) : (
          <div className="space-y-1.5">
            {overview.invoices.items.map((inv) => (
              <RecordLink
                key={inv.id}
                to={`/office/invoices/${encodeURIComponent(inv.id)}`}
                label={inv.invoiceNumber ?? "Invoice"}
                meta={inv.dueDate ? `Due ${formatDate(inv.dueDate)}${inv.daysOverdue > 0 ? ` · ${inv.daysOverdue} days overdue` : ""}` : "No due date on file"}
                trailing={<span className="text-sm text-ele-strong">{gbp(inv.amount)}</span>}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// A row for one merged "open work" item. Links to the LIFT when there is one (the office audit
// records that neither a call-out nor a scheduled visit has a record page of its own yet); a
// lift-less job or emergency call-out renders as plain text instead of guessing at a URL.
function OpenWorkRow({ item }: { item: CustomerOverviewWorkItem }) {
  const meta = [item.date ? formatDate(item.date) : "No date set", item.engineerName ?? "Unassigned"].join(" · ");
  const trailing = <JobTypeBadge type={item.kind === "callout" ? "callout" : item.type} size="sm" />;
  const label = item.liftRef ?? (item.kind === "callout" ? "Call-out" : "Job");

  if (item.liftId) {
    return <RecordLink to={`/office/lifts/${encodeURIComponent(item.liftId)}`} label={label} meta={meta} trailing={trailing} />;
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm">
      <span className="min-w-0">
        <span className="block truncate text-ele-strong">{label}</span>
        <span className="mt-0.5 block truncate text-xs text-ele-body opacity-70">{meta}</span>
      </span>
      <span className="flex-none">{trailing}</span>
    </div>
  );
}

function OpenDefectRow({ defect }: { defect: CustomerOverviewDefect }) {
  const severe = defect.severity === "immediate";
  return (
    <RecordLink
      to={`/office/lifts/${encodeURIComponent(defect.liftId)}`}
      label={defect.description}
      meta={`${defect.liftRef} · Raised ${formatDate(defect.createdAt)}`}
      trailing={<StatusPill word={severe ? "Immediate" : "Advisory"} tone={severe ? "red" : "amber"} variant="solid" />}
    />
  );
}

// The renewal date is its own link (to the contract's Renewal tab); the rest of the row opens the
// contract's own page. Two separate <Link>s side by side, deliberately never one nested in the
// other (RecordLink itself renders an anchor, so this row is NOT built from it).
function ContractRow({ contract }: { contract: CustomerOverviewContract }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm transition hover:border-ele-purple-accent">
      <div className="min-w-0">
        <Link to={`/office/contracts/${encodeURIComponent(contract.id)}`} className="block truncate text-ele-strong hover:underline">
          {contract.siteName}
        </Link>
        <span className="mt-0.5 block truncate text-xs text-ele-body opacity-70">
          {TIER_LABEL[contract.tier] ?? contract.tier} · {gbp(contract.annualValue)}/yr · {CONTRACT_STATUS_LABEL[contract.status] ?? contract.status}
        </span>
      </div>
      {contract.renewalDate ? (
        <Link
          to={`/office/contracts/${encodeURIComponent(contract.id)}?tab=renewal`}
          className="flex-none text-xs text-ele-blue underline-offset-2 hover:underline"
        >
          Renews {formatDate(contract.renewalDate)}
        </Link>
      ) : (
        <span className="flex-none text-xs text-ele-body opacity-60">No renewal date on file</span>
      )}
    </div>
  );
}
